// ###### IMPORTS
import React, { useState, useRef } from 'react';
import { FunnelIcon } from '@heroicons/react/24/solid';
import DropdownCustomDark from 'views/finance/moduleComponents/DropdownCustomDark';
import { formatDate } from 'shared_items/formatConventions';

// ###### COMPONENT
const BoardFilterAssets = ({
  snapshotList = [],
  selectedSnapshotId1 = null,
  onSelectSnapshot1,
  valueMode = 'base',
  onValueModeChange,
  assets = [],
  categories = [],
  selectedAssetIdFilter = null,
  onChangeAssetFilter,
  selectedCategoryCodeFilter = '',
  onChangeCategoryFilter,
  selectedLocationNameFilter = '',
  onChangeLocationFilter,
}) => {
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const dropdown1Ref = useRef(null);
  const assetDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);

  const selectedSnap1 = snapshotList.find((s) => s.id === selectedSnapshotId1);

  const snapshotOptions = [
    { value: '', label: '—' },
    ...snapshotList.map((s) => ({
      value: formatDate(s.snapshot_date, 'display'),
      label: formatDate(s.snapshot_date, 'display'),
    })),
  ];

  const selectedValue1 = selectedSnap1 ? formatDate(selectedSnap1.snapshot_date, 'display') : '';

  const handleSelect1 = (value) => {
    if (value === '') {
      onSelectSnapshot1?.(null);
      return;
    }
    const s = snapshotList.find((snap) => formatDate(snap.snapshot_date, 'display') === value);
    if (s) onSelectSnapshot1?.(s.id);
  };

  const assetOptions = [
    { value: '', label: 'All assets' },
    ...assets.map((a) => ({
      value: String(a.id),
      label: `${a.asset_code ?? a.id} — ${a.asset_name ?? '—'}`,
    })),
  ];
  const selectedAssetOption = selectedAssetIdFilter ? String(selectedAssetIdFilter) : '';

  const categoryOptions = [
    { value: '', label: 'All categories' },
    ...categories.map((c) => ({
      value: c.category_code,
      label: c.category_code,
    })),
  ];

  const locationNames = Array.from(
    new Set(
      assets
        .map((a) => (a.location_name || '').trim())
        .filter((loc) => loc)
    )
  ).sort();
  const locationOptions = [
    { value: '', label: 'All locations' },
    ...locationNames.map((loc) => ({ value: loc, label: loc })),
  ];

  return (
    <div className="bg-gray-600 shadow rounded-xl overflow-hidden mb-2 w-full">
      {/* Header — title left, snapshot + value mode + filter aligned right */}
      <div className="px-4 py-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-white">Assets</h2>
        </div>
        <div className="flex items-center justify-end gap-2 min-w-0 flex-shrink-0 flex-wrap">
          {onValueModeChange && (
            <div className="flex w-[5.5rem] min-w-[5.5rem] shrink-0 items-center gap-2 sm:w-[7.5rem] sm:min-w-[7.5rem]">
              <button
                type="button"
                role="switch"
                aria-checked={valueMode === 'market'}
                aria-label={valueMode === 'market' ? 'Market value' : 'Base value'}
                title={valueMode === 'market' ? 'Showing market value' : 'Showing base value'}
                onClick={() => onValueModeChange(valueMode === 'market' ? 'base' : 'market')}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-0 transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-600 ${
                  valueMode === 'market' ? 'bg-green-500' : 'bg-gray-500 bg-opacity-50'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-4 w-4 shrink-0 rounded-full bg-white shadow ring-0 transition-transform ${
                    valueMode === 'market' ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'
                  }`}
                  aria-hidden
                />
              </button>
              <span className="select-none text-xs text-white opacity-90">
                <span className="sm:hidden">{valueMode === 'market' ? 'market' : 'base'}</span>
                <span className="hidden sm:inline">
                  {valueMode === 'market' ? 'market value' : 'base value'}
                </span>
              </span>
            </div>
          )}
          <div className="relative flex items-center min-w-[120px]" ref={dropdown1Ref}>
            <DropdownCustomDark
              options={snapshotOptions}
              selectedValue={selectedValue1}
              onSelect={handleSelect1}
              placeholder="—"
              width="min-w-[120px]"
              usePortal={true}
              portalRef={dropdown1Ref}
              showRadio={true}
            />
          </div>
          <button
            type="button"
            onClick={() => setIsFilterExpanded((f) => !f)}
            className={`p-1 rounded-full transition-all flex-shrink-0 ${
              isFilterExpanded
                ? 'bg-green-400 text-white'
                : 'bg-gray-500 bg-opacity-30 text-white text-opacity-70 hover:bg-opacity-40'
            }`}
            aria-label="Toggle filters"
          >
            <FunnelIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
      {/* Collapsible section (filters) — mirror Assets Detailed */}
      <div className={`overflow-hidden transition-all duration-200 ease-in-out ${isFilterExpanded ? 'h-10' : 'h-0'}`}>
        <div className="h-10 px-4 flex items-center bg-gray-500 bg-opacity-20 gap-4 flex-wrap">
          <div className="min-w-[140px]" ref={assetDropdownRef}>
            <DropdownCustomDark
              options={assetOptions}
              selectedValue={selectedAssetOption}
              onSelect={(val) => {
                if (!onChangeAssetFilter) return;
                onChangeAssetFilter(val ? Number(val) : null);
              }}
              placeholder="All assets"
              width="min-w-[140px]"
              usePortal={true}
              portalRef={assetDropdownRef}
            />
          </div>
          <div className="min-w-[140px]" ref={categoryDropdownRef}>
            <DropdownCustomDark
              options={categoryOptions}
              selectedValue={selectedCategoryCodeFilter || ''}
              onSelect={(val) => {
                if (!onChangeCategoryFilter) return;
                onChangeCategoryFilter(val || '');
              }}
              placeholder="All categories"
              width="min-w-[140px]"
              usePortal={true}
              portalRef={categoryDropdownRef}
            />
          </div>
          <div className="min-w-[140px]" ref={locationDropdownRef}>
            <DropdownCustomDark
              options={locationOptions}
              selectedValue={selectedLocationNameFilter || ''}
              onSelect={(val) => {
                if (!onChangeLocationFilter) return;
                onChangeLocationFilter(val || '');
              }}
              placeholder="All locations"
              width="min-w-[140px]"
              usePortal={true}
              portalRef={locationDropdownRef}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardFilterAssets;
